<template>
  <div class="mobile-notes-focus">
    <header class="mobile-notes-focus-nav">
      <button
        type="button"
        class="mobile-notes-focus-back"
        :aria-label="$t('memos.focusBack')"
        @click="onBack"
      >
        <i class="bi-chevron-left" aria-hidden="true"></i>
        <span>{{ $t("memos.focusBack") }}</span>
      </button>
      <h1 class="mobile-notes-focus-title">
        {{ noteId ? $t("memos.focusEdit") : $t("memos.focusNew") }}
      </h1>
      <div class="mobile-notes-focus-trail">
        <span class="mobile-notes-focus-status" aria-live="polite">{{ statusLabel }}</span>
        <button
          type="button"
          class="mobile-notes-mode-btn"
          :title="$t('memos.light')"
          :aria-label="$t('memos.light')"
          @click="onSwitchLight"
        >
          <i class="bi-fullscreen-exit" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <div
      class="mobile-notes-focus-view-toggle"
      role="group"
      :aria-label="$t('memos.focusViewMode')"
    >
      <button
        type="button"
        :class="{ active: isEditView }"
        :aria-pressed="isEditView ? 'true' : 'false'"
        @click="setViewMode(FOCUS_VIEW_EDIT)"
      >
        {{ $t("memos.edit") }}
      </button>
      <button
        type="button"
        :class="{ active: isPreviewView }"
        :aria-pressed="isPreviewView ? 'true' : 'false'"
        @click="setViewMode(FOCUS_VIEW_PREVIEW)"
      >
        {{ $t("memos.preview") }}
      </button>
    </div>

    <p v-if="loadError" class="mobile-notes-focus-error">{{ loadError }}</p>

    <div class="mobile-notes-focus-body">
      <template v-if="isPreviewView">
        <div
          v-if="previewEmpty"
          class="mobile-notes-focus-preview-empty"
        >
          {{ $t("memos.previewEmpty") }}
        </div>
        <div
          v-else
          class="mobile-notes-body markdown-body mobile-notes-focus-preview"
          autocomplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          v-html="previewHtml"
          @click="onPreviewClick"
        ></div>
      </template>
      <template v-else>
        <div class="mobile-notes-field mobile-notes-focus-field" :class="{ composing }">
          <div
            class="mobile-notes-highlight"
            aria-hidden="true"
            v-html="highlightMarkup(draft)"
          ></div>
          <textarea
            ref="area"
            v-model="draft"
            class="mobile-notes-textarea mobile-notes-focus-textarea"
            :placeholder="$t('memos.placeholder')"
            spellcheck="false"
            @input="onInput"
            @click="refreshTagSuggest"
            @keyup="onKeyup"
            @scroll="syncScroll"
            @keydown="onKeydown"
            @compositionstart="composing = true"
            @compositionend="onCompositionEnd"
          ></textarea>
        </div>
        <ul
          v-if="showSuggest"
          class="mobile-notes-suggest mobile-notes-suggest-edit"
          role="listbox"
          :aria-label="$t('memos.tagSuggest')"
        >
          <li v-for="(tag, idx) in tagSuggestions" :key="tag">
            <button
              type="button"
              role="option"
              :class="{ active: idx === suggestIndex }"
              :aria-selected="idx === suggestIndex ? 'true' : 'false'"
              @mousedown.prevent="applyTagSuggest(tag)"
            >
              <span class="mobile-notes-suggest-hash">#</span>{{ tag }}
            </button>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
const {
  closeNotesFocus,
  upgradeNotesFocusId,
  takeNotesFocusPendingBody,
  getNotesFocus,
} = require("./notesNav.js");
const {
  FOCUS_VIEW_EDIT,
  FOCUS_VIEW_PREVIEW,
  normalizeFocusViewMode,
  shouldShowFocusSuggest,
} = require("./notesFocusView.js");
const { completeTagSuggestion } = require("./noteTagSuggest.js");
const { createNotesMarkdown } = require("../helpers/createNotesMarkdown.js");
const { neutralizeCredentialPairs } = require("../helpers/notesDisplaySafe.js");

const AUTOSAVE_MS = 1000;

function tagPattern() {
  return /#([\p{L}\p{N}_-]{1,64})/gu;
}

export default {
  name: "MobileNotesFocusPage",
  props: {
    focus: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      md: createNotesMarkdown(),
      FOCUS_VIEW_EDIT,
      FOCUS_VIEW_PREVIEW,
      viewMode: FOCUS_VIEW_EDIT,
      draft: "",
      noteId: null,
      revision: null,
      composing: false,
      saveState: "idle", // idle | saving | saved | error
      loadError: "",
      conflictPaused: false,
      suggestQuery: null,
      suggestStart: -1,
      suggestIndex: 0,
      saveTimer: null,
      leaveLock: false,
      bootToken: 0,
    };
  },
  computed: {
    ...mapGetters(["notesTags"]),
    tags() {
      return this.notesTags || [];
    },
    isEditView() {
      return normalizeFocusViewMode(this.viewMode) === FOCUS_VIEW_EDIT;
    },
    isPreviewView() {
      return normalizeFocusViewMode(this.viewMode) === FOCUS_VIEW_PREVIEW;
    },
    previewEmpty() {
      return !(this.draft && String(this.draft).trim());
    },
    previewHtml() {
      return this.renderBody(this.draft);
    },
    tagSuggestions() {
      if (this.suggestQuery == null) return [];
      const q = this.suggestQuery;
      return (this.tags || [])
        .map((t) => t.tag)
        .filter(Boolean)
        .filter((name) => name.startsWith(q))
        .slice(0, 8);
    },
    showSuggest() {
      return shouldShowFocusSuggest({
        viewMode: this.viewMode,
        composing: this.composing,
        suggestionCount: this.tagSuggestions.length,
      });
    },
    statusLabel() {
      if (this.saveState === "saving") return this.$t("memos.saving");
      if (this.saveState === "saved") return this.$t("memos.saved");
      return "";
    },
  },
  watch: {
    focus: {
      immediate: true,
      handler(next) {
        this.bootFromFocus(next);
      },
    },
  },
  beforeUnmount() {
    this.clearSaveTimer();
  },
  methods: {
    async bootFromFocus(focus) {
      const token = ++this.bootToken;
      this.clearSaveTimer();
      this.conflictPaused = false;
      this.loadError = "";
      this.saveState = "idle";
      this.viewMode = FOCUS_VIEW_EDIT;
      this.clearTagSuggest();
      const nav = focus || getNotesFocus() || { noteId: null };
      const id = nav.noteId || null;
      this.noteId = id;
      this.revision = null;
      const pending = takeNotesFocusPendingBody();
      if (id) {
        try {
          const note = await this.$store.dispatch("notesLoadOne", { id });
          if (token !== this.bootToken) return;
          this.draft = pending != null ? pending : (note && note.body) || "";
          this.revision = note && note.revision != null ? note.revision : null;
          if (pending != null) this.scheduleSave();
        } catch (err) {
          if (token !== this.bootToken) return;
          this.draft = pending != null ? pending : "";
          this.loadError = (err && err.message) || this.$t("memos.loadFailed");
        }
      } else {
        this.draft = pending == null ? "" : pending;
      }
      this.$nextTick(() => this.focusEditor());
    },
    async setViewMode(mode) {
      const next = normalizeFocusViewMode(mode);
      if (next === normalizeFocusViewMode(this.viewMode)) return;
      if (next === FOCUS_VIEW_PREVIEW) {
        this.clearTagSuggest();
        await this.flushSave();
        this.viewMode = FOCUS_VIEW_PREVIEW;
        return;
      }
      this.viewMode = FOCUS_VIEW_EDIT;
      this.$nextTick(() => this.focusEditor());
    },
    focusEditor() {
      const el = this.$refs.area;
      if (!el) return;
      el.focus();
      this.syncFieldHeight();
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    highlightMarkup(text) {
      const raw = neutralizeCredentialPairs(text == null ? "" : String(text));
      if (!raw) return "";
      const escaped = this.escapeHtml(raw);
      const withTags = escaped.replace(tagPattern(), '<span class="mobile-notes-htag-live">#$1</span>');
      return withTags.replace(/\n$/g, "\n&nbsp;").replace(/\n/g, "<br>");
    },
    linkifyTagsInHtml(html) {
      return html.replace(/(^|>)([^<]*)/g, (full, prefix, text) => {
        const linked = text.replace(tagPattern(), (_, tag) => {
          const safe = this.escapeHtml(tag);
          return `<button type="button" class="mobile-notes-htag" data-tag="${safe}">#${safe}</button>`;
        });
        return prefix + linked;
      });
    },
    renderBody(body) {
      const html = this.md.render(neutralizeCredentialPairs(body || ""));
      return this.linkifyTagsInHtml(html);
    },
    async onPreviewClick(event) {
      const btn = event.target.closest(".mobile-notes-htag");
      if (!btn) return;
      event.preventDefault();
      const tag = btn.getAttribute("data-tag");
      if (!tag) return;
      await this.flushSave();
      await this.$store.dispatch("notesSetTagFilter", tag);
      closeNotesFocus();
    },
    clearSaveTimer() {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }
    },
    scheduleSave() {
      if (this.conflictPaused || this.composing) return;
      this.clearSaveTimer();
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this.flushSave();
      }, AUTOSAVE_MS);
    },
    async flushSave() {
      this.clearSaveTimer();
      if (this.conflictPaused || this.saveState === "saving") return;
      const body = this.draft == null ? "" : String(this.draft);
      if (!this.noteId && !body.trim()) return;
      this.saveState = "saving";
      try {
        if (!this.noteId) {
          const note = await this.$store.dispatch("notesCreate", { body });
          this.noteId = note.id;
          this.revision = note.revision;
          upgradeNotesFocusId(note.id);
        } else {
          const note = await this.$store.dispatch("notesUpdate", {
            id: this.noteId,
            body,
            baseRevision: this.revision,
          });
          this.revision = note.revision;
        }
        this.saveState = "saved";
      } catch (err) {
        if (err && err.status === 409 && err.note) {
          this.conflictPaused = true;
          this.draft = err.note.body || "";
          this.revision = err.note.revision;
          this.noteId = err.note.id;
          this.loadError = this.$t("memos.conflict");
          this.saveState = "error";
          return;
        }
        this.saveState = "error";
      }
    },
    async leave() {
      if (this.leaveLock) return;
      this.leaveLock = true;
      try {
        await this.flushSave();
        closeNotesFocus();
      } finally {
        this.leaveLock = false;
      }
    },
    onBack() {
      this.leave();
    },
    onSwitchLight() {
      this.leave();
    },
    onCompositionEnd() {
      this.composing = false;
      this.onInput();
    },
    onInput() {
      this.syncFieldHeight();
      this.$nextTick(() => {
        this.syncScroll();
        this.refreshTagSuggest();
      });
      if (!this.composing) this.scheduleSave();
    },
    onKeyup(event) {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        this.refreshTagSuggest();
      }
    },
    onKeydown(event) {
      if (this.showSuggest) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.suggestIndex = (this.suggestIndex + 1) % this.tagSuggestions.length;
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          this.suggestIndex =
            (this.suggestIndex - 1 + this.tagSuggestions.length) % this.tagSuggestions.length;
          return;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          const tag = this.tagSuggestions[this.suggestIndex];
          if (tag) this.applyTagSuggest(tag);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          this.clearTagSuggest();
          return;
        }
      }
    },
    getTagQueryAtCursor(text, cursor) {
      const before = String(text || "").slice(0, cursor);
      const m = before.match(/#([\p{L}\p{N}_-]*)$/u);
      if (!m) return null;
      return { start: m.index, query: m[1] };
    },
    refreshTagSuggest() {
      if (!this.isEditView) {
        this.clearTagSuggest();
        return;
      }
      const ta = this.$refs.area;
      if (!ta || this.composing) {
        this.clearTagSuggest();
        return;
      }
      const found = this.getTagQueryAtCursor(this.draft, ta.selectionStart || 0);
      if (!found) {
        this.clearTagSuggest();
        return;
      }
      this.suggestQuery = found.query;
      this.suggestStart = found.start;
      this.suggestIndex = 0;
    },
    clearTagSuggest() {
      this.suggestQuery = null;
      this.suggestStart = -1;
      this.suggestIndex = 0;
    },
    applyTagSuggest(tag) {
      const ta = this.$refs.area;
      if (!ta || this.suggestStart < 0) return;
      const text = this.draft;
      const cursor = ta.selectionStart || 0;
      const start = this.suggestStart;
      const { text: next, caret } = completeTagSuggestion(text, start, cursor, tag);
      this.clearTagSuggest();
      ta.focus();
      if (typeof ta.setRangeText === "function") {
        ta.setRangeText(`#${tag}\n`, start, cursor, "end");
        this.draft = ta.value;
      } else {
        this.draft = next;
        ta.value = next;
      }
      this.$nextTick(() => {
        const pos = Math.min(caret, (ta.value || "").length);
        ta.focus();
        ta.setSelectionRange(pos, pos);
        this.onInput();
        requestAnimationFrame(() => {
          ta.setSelectionRange(pos, pos);
        });
      });
    },
    syncScroll() {
      const ta = this.$refs.area;
      const field = ta && ta.parentElement;
      const hl = field && field.querySelector(".mobile-notes-highlight");
      if (!ta || !hl) return;
      hl.scrollTop = ta.scrollTop;
      hl.scrollLeft = ta.scrollLeft;
    },
    syncFieldHeight() {
      const el = this.$refs.area;
      if (!el) return;
      // Fill remaining viewport; highlight mirrors scroll, not auto-grow height
      el.style.height = "100%";
      const hl = el.parentElement && el.parentElement.querySelector(".mobile-notes-highlight");
      if (hl) hl.style.height = "100%";
    },
  },
};
</script>
